import React, { useState, useEffect, useRef } from "react";
import { CameraIcon, TrashIcon, UploadIcon } from "lucide-react";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";

// Static avatar options
const staticAvatars = [
  "/avatars/ava1.png",
  "/avatars/ava2.png",
  "/avatars/ava3.png",
  "/avatars/ava4.png",
  "/avatars/ava5.png",
  "/avatars/ava6.png",
  "/avatars/ava7.png",
  "/avatars/ava8.png",
  "/avatars/ava9.png",
];

export const SettingsAvatarSection: React.FC = () => {
  const { user, uploadUserAvatar, updateUserProfile, removeUserAvatar } =
    useAuth();

  // Avatar state
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState<string | null>(null);
  const [currentAvatarDisplayUrl, setCurrentAvatarDisplayUrl] =
    useState<string>("/avatars/ava1.png");
  const [isSavingAvatar, setIsSavingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [hasUnsavedAvatarChanges, setHasUnsavedAvatarChanges] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize avatar display when user is available
  useEffect(() => {
    if (user?.profile) {
      setCurrentAvatarDisplayUrl(
        user.profile.avatar_url || "/avatars/ava1.png"
      );
    }
  }, [user]);

  // Monitor avatar changes
  useEffect(() => {
    if (!user?.profile) return;

    const avatarChanged =
      avatarFile !== null ||
      currentAvatarDisplayUrl !==
        (user.profile.avatar_url || "/avatars/ava1.png");

    setHasUnsavedAvatarChanges(avatarChanged);
  }, [currentAvatarDisplayUrl, avatarFile, user?.profile]);

  // Cleanup preview URL when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (avatarPreviewUrl) {
        URL.revokeObjectURL(avatarPreviewUrl);
      }
    };
  }, [avatarPreviewUrl]);

  // Check if current avatar is a custom uploaded file (not a static avatar)
  const isCustomUploadedAvatar = () => {
    const currentSavedAvatar = user?.profile?.avatar_url;
    return (
      currentSavedAvatar &&
      !staticAvatars.includes(currentSavedAvatar) &&
      currentSavedAvatar !== "/avatars/ava1.png" &&
      currentSavedAvatar.includes("/storage/v1/object/public/avatars/")
    );
  };

  // Handle static avatar selection
  const handleSelectStaticAvatar = (avatarPath: string) => {
    // Clear any pending file upload
    setAvatarFile(null);
    if (avatarPreviewUrl) {
      URL.revokeObjectURL(avatarPreviewUrl);
      setAvatarPreviewUrl(null);
    }

    // Update current avatar display
    setCurrentAvatarDisplayUrl(avatarPath);

    // Clear any errors
    setAvatarError(null);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle avatar file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setAvatarError(null);

    if (!file) {
      setAvatarFile(null);
      if (avatarPreviewUrl) {
        URL.revokeObjectURL(avatarPreviewUrl);
        setAvatarPreviewUrl(null);
      }
      return;
    }

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      setAvatarError("Format file tidak didukung. Gunakan JPG, PNG, atau GIF.");
      setAvatarFile(null);
      return;
    }

    // Validate file size (2MB limit)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      setAvatarError("Ukuran file terlalu besar. Maksimal 2MB.");
      setAvatarFile(null);
      return;
    }

    // Create preview URL
    if (avatarPreviewUrl) {
      URL.revokeObjectURL(avatarPreviewUrl);
    }
    const previewUrl = URL.createObjectURL(file);

    setAvatarFile(file);
    setAvatarPreviewUrl(previewUrl);
    setCurrentAvatarDisplayUrl(previewUrl);
  };

  // Handle avatar removal (reset to default)
  const handleRemoveAvatar = async () => {
    if (!user?.id) return;

    setIsSavingAvatar(true);
    setAvatarError(null);

    try {
      // Use the removeUserAvatar function which handles both storage cleanup and database update
      const result = await removeUserAvatar(user.id);

      if (result.success) {
        // Clear file selection and preview
        setAvatarFile(null);
        if (avatarPreviewUrl) {
          URL.revokeObjectURL(avatarPreviewUrl);
          setAvatarPreviewUrl(null);
        }

        // Reset to default static avatar
        setCurrentAvatarDisplayUrl("/avatars/ava1.png");

        // Clear file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        // Clear unsaved changes flag
        setHasUnsavedAvatarChanges(false);
      } else {
        setAvatarError(result.error || "Gagal menghapus avatar");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Terjadi kesalahan";
      setAvatarError(errorMessage);
    } finally {
      setIsSavingAvatar(false);
    }
  };

  // Handle avatar changes submission
  const handleSubmitAvatarChanges = async () => {
    if (!user?.id) return;

    setIsSavingAvatar(true);
    setAvatarError(null);

    try {
      if (avatarFile) {
        // Upload new custom avatar (this will automatically clean up old custom avatar)
        const uploadResult = await uploadUserAvatar(user.id, avatarFile);
        if (!uploadResult.success) {
          setAvatarError(uploadResult.error || "Gagal mengunggah avatar");
          return;
        }

        // Clear file selection after successful upload
        setAvatarFile(null);
        if (avatarPreviewUrl) {
          URL.revokeObjectURL(avatarPreviewUrl);
          setAvatarPreviewUrl(null);
        }
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        // User selected a static avatar - clean up old custom avatar if needed
        const currentAvatarUrl = user.profile?.avatar_url;
        const isCurrentCustom =
          currentAvatarUrl &&
          !staticAvatars.includes(currentAvatarUrl) &&
          currentAvatarUrl !== "/avatars/ava1.png" &&
          currentAvatarUrl.includes("/storage/v1/object/public/avatars/");

        if (isCurrentCustom) {
          // Remove old custom avatar from storage before updating to static avatar
          const removeResult = await removeUserAvatar(user.id);
          if (!removeResult.success) {
            setAvatarError(removeResult.error || "Gagal menghapus avatar lama");
            return;
          }
        }

        // Update to selected static avatar
        const result = await updateUserProfile(user.id, {
          name: user.profile?.name || "",
          asal_sekolah: user.profile?.asal_sekolah || "",
          role: user.profile?.role || "",
          avatar_url: currentAvatarDisplayUrl,
        });

        if (!result.success && result.error) {
          setAvatarError(result.error);
          return;
        }
      }

      // Clear unsaved changes flag on success
      setHasUnsavedAvatarChanges(false);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Terjadi kesalahan";
      setAvatarError(errorMessage);
    } finally {
      setIsSavingAvatar(false);
    }
  };

  return (
    <div className="bg-gray-50 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
      <h3 className="text-lg sm:text-xl font-semibold text-[#373e46] mb-4">
        Avatar Profil
      </h3>

      <div className="flex flex-col sm:flex-row items-center gap-6">
        {/* Current Avatar Display */}
        <div className="relative">
          <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden bg-gray-200 border-4 border-white shadow-lg">
            {avatarPreviewUrl ? (
              <img
                src={avatarPreviewUrl}
                alt="Avatar Preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <img
                src={currentAvatarDisplayUrl}
                alt="Current Avatar"
                className="w-full h-full object-cover"
              />
            )}
          </div>

          {/* Loading overlay */}
          {isSavingAvatar && (
            <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          )}
        </div>

        {/* Avatar Controls */}
        <div className="flex-1 space-y-4">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif"
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* File selection and action buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={isSavingAvatar}
              variant="outline"
              className="flex items-center gap-2 text-[#373e46] border-gray-300 hover:bg-gray-50"
            >
              <CameraIcon className="w-4 h-4" />
              Pilih Foto
            </Button>

            {/* Only show Reset ke Default button for custom uploaded avatars and no pending changes */}
            {isCustomUploadedAvatar() && !hasUnsavedAvatarChanges && (
              <Button
                onClick={handleRemoveAvatar}
                disabled={isSavingAvatar}
                variant="outline"
                className="flex items-center gap-2 text-red-600 border-red-300 hover:bg-red-50"
              >
                <TrashIcon className="w-4 h-4" />
                Reset ke Default
              </Button>
            )}

            {/* Avatar Save Button - appears when there are unsaved avatar changes */}
            {hasUnsavedAvatarChanges && (
              <Button
                onClick={handleSubmitAvatarChanges}
                disabled={isSavingAvatar}
                className="flex items-center gap-2 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white"
              >
                <UploadIcon className="w-4 h-4" />
                {isSavingAvatar ? "Menyimpan..." : "Simpan Perubahan"}
              </Button>
            )}
          </div>

          {/* Avatar error display */}
          {avatarError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">{avatarError}</p>
            </div>
          )}

          {/* File info */}
          <div className="text-xs text-gray-500">
            <p>Format yang didukung: JPG, PNG, GIF</p>
            <p>Ukuran maksimal: 2MB</p>
            <p>Gambar akan diubah ukurannya menjadi 200x200 piksel</p>
          </div>
        </div>
      </div>

      {/* Static Avatar Options */}
      <div className="mt-6">
        <h4 className="text-sm font-medium text-[#373e46] mb-3">
          Pilih Avatar
        </h4>
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-3">
          {staticAvatars.map((avatarPath, index) => (
            <button
              key={index}
              onClick={() => handleSelectStaticAvatar(avatarPath)}
              disabled={isSavingAvatar}
              className={`relative w-16 h-16 rounded-full overflow-hidden border-2 transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${
                currentAvatarDisplayUrl === avatarPath && !avatarPreviewUrl
                  ? "border-[#6b2cfe] ring-2 ring-[#6b2cfe] ring-opacity-50"
                  : "border-gray-300 hover:border-[#6b2cfe]"
              }`}
            >
              <img
                src={avatarPath}
                alt={`Avatar option ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
